import React from 'react';
import type { FieldValues } from 'react-hook-form';
import ConstituentQuickFilter from '@/constants/constituents/ConstituentQuickFilter';
import OpportunityFilter from '@/constants/opportunities/OpportunityQuickFilter';
import useMobileView from '@/hooks/ui/useMobileView';
import translate from '@/lib/intl/translate';
import { DataListTableHeader } from '@/modules/DataList';
import { useQueryProvider } from '@/modules/QueryProvider';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import {
  QuickFilter,
  QuickFilterConstituent,
  QuickFilterOpportunity,
} from '@/modules/QuickFilters';
import type Label from '@/types/Label';
import useGroupOpportunitiesFilterDefaultValues from '../hooks/useGroupOpportunitiesFilterDefaultValues';
import useOpportunitiesDataListData from '../hooks/useOpportunitiesDataListData';
import useOpportunitiesFilterDefaultValues from '../hooks/useOpportunitiesFilterDefaultValues';
import type OpportunitiesFilterParams from '../types/OpportunitiesFilterParams';
import QuickFilterGroupedOpportunities from './QuickFilterGroupedOpportunities';

export default function OpportunitiesDataListHeader() {
  const isMobile = useMobileView();
  const { totalHits } = useOpportunitiesDataListData();
  const { updateQuery } = useQueryProvider<OpportunitiesFilterParams>();

  const { constituentDefaultValues, opportunityDefaultValues } =
    useOpportunitiesFilterDefaultValues();
  const groupOpportunitiesDefaultValues =
    useGroupOpportunitiesFilterDefaultValues();

  const handleSubmit = (values: FieldValues) => {
    updateQuery({
      constituentType: values[ConstituentQuickFilter.CONSTITUENT_TYPE],
      constituentCategories:
        values[ConstituentQuickFilter.CONSTITUENT_CATEGORIES],
      assigneeIds: values[ConstituentQuickFilter.ASSIGNEE_IDS],
      constituentLabelIds: values[
        ConstituentQuickFilter.CONSTITUENT_LABEL_IDS
      ].map(({ labelId }: Label) => labelId),
    });
  };

  const handleGroupOpportunitiesSubmit = (values: FieldValues) => {
    updateQuery({
      groupingType: values.groupingType,
    });
  };

  const handleOpportunitySubmit = (values: FieldValues) => {
    updateQuery({
      archived: values[OpportunityFilter.ARCHIVED],
      campaignId: values[OpportunityFilter.CAMPAIGN_ID],
      confidenceLevelIds: values[OpportunityFilter.CONFIDENCE_LEVEL_IDS],
      opportunityLabelIds: values[OpportunityFilter.OPPORTUNITY_LABEL_IDS].map(
        ({ labelId }: Label) => labelId,
      ),
      opportunityStageIds: values[OpportunityFilter.OPPORTUNITY_STAGE_IDS],
      ownerIds: values[OpportunityFilter.OWNER_IDS],
    });
  };

  return (
    <div className="mb-6 flex flex-col justify-between lg:flex-row lg:items-center">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-1">
        <DataListTableHeader
          text="OPPORTUNITIES_TABLE_HEADER"
          count={totalHits}
        />
        {isMobile && (
          <QuickFilter
            buttonContent={translate('OPPORTUNITIES_GROUP_BY')}
            label={translate('OPPORTUNITIES_GROUP_BY_LABEL')}
            handleSubmit={handleGroupOpportunitiesSubmit}
            defaultValues={groupOpportunitiesDefaultValues}
          >
            <QuickFilterGroupedOpportunities />
          </QuickFilter>
        )}
      </div>
      <div className=" grid grid-cols-2 gap-2 md:flex md:justify-end md:gap-4">
        {!isMobile && (
          <QuickFilter
            buttonContent={translate('OPPORTUNITIES_GROUP_BY')}
            label={translate('OPPORTUNITIES_GROUP_BY_LABEL')}
            handleSubmit={handleGroupOpportunitiesSubmit}
            defaultValues={groupOpportunitiesDefaultValues}
          >
            <QuickFilterGroupedOpportunities />
          </QuickFilter>
        )}
        <QuickFilter
          buttonContent={translate('OPPORTUNITY')}
          label={translate('OPPORTUNITIES_FILTERS')}
          handleSubmit={handleOpportunitySubmit}
          defaultValues={opportunityDefaultValues}
        >
          <QuickFilterOpportunity />
        </QuickFilter>
        <QuickFilter
          buttonContent={translate('CONSTITUENT')}
          label={translate('CONSTITUENT_FILTERS')}
          handleSubmit={handleSubmit}
          defaultValues={constituentDefaultValues}
        >
          <QuickFilterConstituent constituentCategoriesV2 />
        </QuickFilter>
      </div>
    </div>
  );
}
